import React from 'react';

import { AlineTransforms } from '@aline/common';
import type { DrawReactComponentProps } from '@aline/common';
import { ButtonBase } from '@mui/material';
import { Editor } from 'slate';
import { AlineTooltip } from 'src/components';
import { ClientTransforms } from 'src/editor/transforms';
import { useEditor } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

const TRIANGLE_SIZE = 12;
const BUTTON_SIZE = TRIANGLE_SIZE * Math.sqrt(2);

interface StyleProps {
  isCollapsed: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isCollapsed }) => ({
  button: {
    borderRadius: '100%',
    height: BUTTON_SIZE,
    transform: 'translate(-50%)',
    width: BUTTON_SIZE,
  },
  triangle: {
    fill: theme.appColors.primary,
    transform: isCollapsed ? 'rotate(-45deg)' : undefined,
    transition: 'transform 200ms ease',
  },
}));

export function ToggleDynamicBlockButton({
  dynamicBlockEntry,
  isCollapsed,
}: DrawReactComponentProps['ToggleDynamicBlockButton']): JSX.Element | null {
  const [, path] = dynamicBlockEntry;
  const editor = useEditor();
  const { classes } = useStyles({ isCollapsed });

  function toggleCollapsed(event: React.MouseEvent) {
    stopPropagation(event);

    ClientTransforms.setDynamicBlockCollapsed(editor, {
      dynamicBlockEntry,
      isCollapsed,
    });

    const blockStart = Editor.start(editor, path);
    const point = isCollapsed ? blockStart : Editor.before(editor, blockStart);
    if (point) AlineTransforms.select(editor, point);
    ClientTransforms.focus(editor);
  }

  return (
    <AlineTooltip
      arrow
      placement="top"
      title={isCollapsed ? 'Show hidden block' : 'Hide collapsible block'}
    >
      <ButtonBase
        className={classes.button}
        onClick={toggleCollapsed}
        // Capture events so they don't register as selection page clicks.
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
      >
        <svg
          className={classes.triangle}
          height={TRIANGLE_SIZE}
          viewBox="0 0 24 24"
          width={TRIANGLE_SIZE}
        >
          {/* ◢ */}
          <path d="M 24 0 L 24 24 L 0 24 Z" />
        </svg>
      </ButtonBase>
    </AlineTooltip>
  );
}

function stopPropagation(event: React.MouseEvent) {
  event.stopPropagation();
}
